/* eslint-disable import/prefer-default-export */
import { store } from 'store';
import { discartNullValues } from 'utils/formatValues';

// Solo funciona para Oaxaca.
const OAXACA = process.env.REACT_APP_ENV_NAME === 'oaxaca';

/**
 * Rellena una clave con ceros hasta alcanzar la longitud especificada.
 * @param {Object} params - Parámetros de la clave.
 * @param {string} params.prefix - Prefijo de la clave.
 * @param {string} params.suffix - Sufijo de la clave.
 * @param {number} params.length - Longitud total de la clave.
 * @returns {string} Clave formateada con ceros.
 */
const fillWithZeros = ({ prefix = '', suffix = '', length = 13 }) => {
  const zeros = '0'.repeat(length - prefix.length - suffix.length);
  return `${prefix}${zeros}${suffix}`;
};

/**
 * Obtiene la configuración de la clave de padrón principal desde el store.
 * @returns {{ clave: string } | null} Objeto con la clave de padrón o null si no se encuentra.
 */
const getConfigClaveDePredio = () => {
  const { llavesDePredios, tiposDeClavesDePredios } = store.getState().catalogs;

  const config = llavesDePredios.find((e) => e.es_principal);
  if (!config) return null;

  return tiposDeClavesDePredios.find((e) => e.id === config.clave_de_predio) || null;
};

/**
 * Obtiene el valor de un campo del formulario.
 * @param {string} clave - Nombre de la clave en el formulario.
 * @param {Object} formInstance - Instancia del formulario.
 * @returns {string} Valor del campo o una cadena vacía si no existe.
 */
const getFieldValue = (clave, formInstance) => {
  const values = formInstance.getFieldsValue();
  return values?.[clave] || '';
};

/**
 * Formatea una clave de padrón agregando ceros intermedios según el formato esperado.
 * @param {string} value - Valor ingresado por el usuario.
 * @param {number} [maxLength=13] - Longitud máxima de la clave.
 * @returns {string | null} Clave formateada o null si el formato no es válido.
 */
export const formatClaveDePredio = (value, maxLength = 13) => {
  if (!value || !/\./.test(value)) return value;

  const [prefix, suffix] = value.split('.');
  return fillWithZeros({ prefix, suffix, length: maxLength });
};

/**
 * Maneja el evento onBlur para actualizar el valor de la clave formateada en el formulario.
 * @param {Object} formInstance - Instancia del formulario.
 * @param {string} [key=''] - Clave específica a formatear (opcional).
 */
export const handleOnBlur = async (formInstance = null, key = '') => {
  try {
    if (!formInstance || !OAXACA) return;

    const claveDePredio = getConfigClaveDePredio();
    if (!claveDePredio) return;

    const { clave, longitud_maxima } = claveDePredio;
    const internalKey = key || clave;
    const value = getFieldValue(internalKey, formInstance);

    const newValue = formatClaveDePredio(value, longitud_maxima);
    if (!newValue) return;

    formInstance.setFieldsValue({ [internalKey]: newValue });
    await formInstance.validateFields([internalKey]);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

const VALID_KEYS = [
  'clave',
  'clave_catastral_municipal',
  'q',
];

/**
 * Formats the value of the first valid key in the params object.
 * Valid keys are defined in the `VALID_KEYS` array.
 *
 * @param {Object} params - The parameters object to process.
 * @returns {void} - Updates the `params` object in place.
 */
export const formatPredioKeyProp = (params) => {
  // Discard null or undefined values from params
  const filteredParams = discartNullValues(params);

  // Find all keys in the params object that are valid
  const keys = Object.keys(filteredParams);

  // Find and process the first valid key
  VALID_KEYS.forEach((key) => {
    if (keys.includes(key)) {
      // Get the value associated with the key
      const value = filteredParams[key];

      // Format the value using formatClaveDePredio
      const formattedValue = formatClaveDePredio(value);

      // Update the params object with the formatted value
      // eslint-disable-next-line no-param-reassign
      params[key] = formattedValue;
    }
  });
};
